import { makeAutoObservable } from "mobx";
import TaskAnalysis from "./TaskAnalysis";
import Analysis from "./Analysis";
import { currencyFormat, decimalAsPercentFormat } from "../util/Formatters";

export default class TaskPackageAnalysis {
  constructor(
    project,
    taskPackage,
    id,
    name,
    plannedStartDate,
    plannedEndDate,
    tasks,
    taskPackages,
    periodEntries,
    code
  ) {
    this.project = project;
    this.taskPackage = taskPackage;
    this.id = id;
    this.name = name;
    this.plannedStartDate = plannedStartDate;
    this.plannedEndDate = plannedEndDate;
    this.tasks = tasks != null ? tasks : [];
    this.taskPackages = taskPackages != null ? taskPackages : [];
    this.periodEntries = periodEntries != null ? periodEntries : [];
    this.displayIndex = 0;
    this.code = code;
    makeAutoObservable(this);
  }

  getAnalysis(taskId) {
    let filteredTasks = this.tasks.filter((value) => value.id === taskId);
    if (filteredTasks.length > 0) {
      return filteredTasks[0];
    } else if (this.id === taskId) {
      return this;
    } else {
      if (this.taskPackages.length > 0) {
        for (let i = 0; i < this.taskPackages.length; i++) {
          let packageAnalysis = this.taskPackages[i];
          if (packageAnalysis.id === taskId) {
            return packageAnalysis;
          }
          let task = packageAnalysis.getAnalysis(taskId);
          if (task != null) {
            return task;
          }
        }
      }
    }
  }

  countChildren() {
    let count = 0;
    count += this.tasks.length;
    count += this.taskPackages.length;
    for (const pack of this.taskPackages) {
      count += pack.countChildren();
    }
    return count;
  }

  buildReportRows(depth, specifiedPeriod, fullProject) {
    const resultRows = [];
    let thisRow = {};
    let facSum = 0;
    const zeroPercent = 0.0;
    const zeroDollars = (0);
    const zero = 0;
    
    const refTaskPackage = fullProject.rootTaskPackage.getTaskPackage(this.id);
    // This sections of code will only be executed for taskPackages, avoiding all task
    thisRow.id = this.id
    thisRow.displayName = " ".repeat(depth * 3) + this.code + " " + this.name;
    thisRow.progress = zeroPercent;
    thisRow.budgetSpent = zeroPercent;
    thisRow.plannedProgress = zeroPercent;
    thisRow.bac = zeroDollars;
    thisRow.actualCost = zeroDollars;
    thisRow.actualCostPeriod = zeroDollars;
    thisRow.cpiCumulativeIcon = "";
    thisRow.cpiCumulative = zero.toFixed(2);
    thisRow.spiCumulativeIcon = "";
    thisRow.spiCumulative = zero.toFixed(2);
    // The "facColumn" needs to be checked, not sure about it
    thisRow.fac = zeroDollars;
    thisRow.vac = zeroDollars;
    thisRow.costStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
    thisRow.schedStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
    thisRow.comment = "";
    thisRow.sortIndex = this.displayIndex;
    if (Math.min(...refTaskPackage.actualProgress.map(ap => +ap.period )) <= +specifiedPeriod) {
        thisRow.started = true;
      } else {
        thisRow.started = false;
      }

    const analysis = this.analysis.find(e => e.period * 1 === specifiedPeriod * 1)
    if (analysis != null) {
      let result =
        analysis.actualProgress *
        analysis.budgetSpent *
        analysis.plannedProgress;
      let plannedProgressCol = analysis.plannedProgress; // "IFERROR({0}/{1},0)", worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.bacColumn].Address);
      let bacCol = analysis.budgetAtCompletion;
      let progressCol = analysis.actualProgress;
      // The "earnedValueCol" needs to be checked, not sure about it
      let earnedValueCol = analysis.earnedValue; // "{0}+{1}-{2}", worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.bacColumn].Address, worksheet.Cells[row, this.facColumn].Address);
      let actualCostCol = analysis.actualCost;
      let budgetSpentCol = analysis.budgetSpent; // analysis.budgetSpent = ("IFERROR({0}/{1},0)", worksheet.Cells[row, this.].Address, worksheet.Cells[row, this.bacColumn].Address)
      let actualCostPeriodCol = analysis.actualCostPeriod;
      let earnedValuePeriodCol = analysis.earnedValuePeriod;
      

      // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
      // however as it is not being used in our current version, I have be commented it out
      //let costVarianceCol = analysis.costVariance; // analysis.costVariance = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)
      //let earnedScheduleCol = analysis.earnedSchedule;
      //let scheduleVarianceCol = analysis.scheduleVarianceCost; // analysis.scheduleVarianceCost = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address)

      // The "cpiCumuCol" needs to be checked, not sure about it as it's being calculated from the eranedValue
      let cpiCumuCol = analysis.costPerformanceIndex; // (analysis.costPerformanceIndex).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),0)", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)

      let cpiCumuColNeg = "";
      let spiCostCumuColNeg = "";

      // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
      // however as it is not being used in our current version, I have be commented it out
      //let spiTimeCumuColNeg = "";
      if (result !== 0 && !isNaN(result)) {
        // "IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address
        let calcCpi = 0;
        if (actualCostCol - actualCostPeriodCol > 0) {
          calcCpi =
            cpiCumuCol -
            (earnedValueCol - earnedValuePeriodCol) /
            (actualCostCol - actualCostPeriodCol);
        }

        let calcSpiCost =
          analysis.schedulePerformanceIndexCostPeriod -
          analysis.schedulePerformanceIndexCost; // "ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

        // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
        // however as it is not being used in our current version, I have be commented it out
        //let calcSpiTime = analysis.schedulePerformanceIndexTimePeriod - analysis.schedulePerformanceIndexTime; // "(ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

        if (calcCpi > 0) {
          cpiCumuColNeg = "\u25B2";
        } // "IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>0,\"\u25B2\",IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>=-0.0001,\"\u25AC\",\"\u25BC\"))", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address)
        else if (calcCpi >= -0.0001) {
          cpiCumuColNeg = "\u25AC";
        } else {
          cpiCumuColNeg = "\u25BC";
        }

        if (calcSpiCost > 0) {
          spiCostCumuColNeg = "\u25B2";
        } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
        else if (calcSpiCost >= -0.0001) {
          spiCostCumuColNeg = "\u25AC";
        } else {
          spiCostCumuColNeg = "\u25BC";
        }

        // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
        // however as it is not being used in our current version, I have be commented it out
        /*if (calcSpiTime > 0) {
          spiTimeCumuColNeg = "\u25B2";
        } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
        else if (calcSpiTime >= -0.0001) {
          spiTimeCumuColNeg = "\u25AC";
        } else {
          spiTimeCumuColNeg = "\u25BC";
        }*/
      }

      // The "spiCostCol" and "spiTimeCol" needs to be checked, not sure about them
      let spiCol = analysis.weightedAverageSchedulePerformanceIndexTime; // (analysis.schedulePerformanceIndexCost).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),ROUND(1+{2},2))", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.progressColumn].Address)

      // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
      // however as it is not being used in our current version, I have be commented it out
      //let spiTimeCol = analysis.schedulePerformanceIndexTime;
      let vacCol = analysis.varianceAtCompletionMean;
      let costStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
      let schedStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
      let commentCol = analysis.note;

      thisRow.progress = progressCol;
      thisRow.budgetSpent = budgetSpentCol;
      thisRow.plannedProgress = plannedProgressCol;
      thisRow.bac = (bacCol);
      thisRow.actualCost = (actualCostCol);
      thisRow.actualCostPeriod = (actualCostPeriodCol);
      thisRow.cpiCumulativeIcon = cpiCumuColNeg;
      thisRow.cpiCumulative =
        cpiCumuCol != null ? cpiCumuCol.toFixed(2) : cpiCumuCol;
      thisRow.spiCumulativeIcon = spiCostCumuColNeg;
      thisRow.spiCumulative =
        spiCol != null ? spiCol.toFixed(2) : spiCol;
      // The "facColumn" needs to be checked, not sure about it
      thisRow.fac = (analysis.budgetedRateForecast);
      thisRow.vac = (vacCol);
      thisRow.costStatusIndicator = costStatusCol;
      thisRow.schedStatusIndicator = schedStatusCol;
      thisRow.comment = commentCol;
    }
    thisRow.styles = {}
    thisRow.styles.displayName = " " + this.code + " " + this.name + " ";
    thisRow.styles.fontSize = Math.max(14 - depth, 11);
    if (thisRow.styles.fontSize < 11) {
      alert("WTF Mates!");
    }
    thisRow.styles.fontStyle = depth < 3 ? "bold" : "plain";
    thisRow.styles.treeDepth = depth * 1;
    thisRow.styles.childCount = this.countChildren();
    thisRow.styles.sortIndex = this.displayIndex;

    const aggregateChildren = []
    aggregateChildren.push(... this.taskPackages);
    aggregateChildren.push(... this.tasks);
    aggregateChildren.sort((a, b) => a.displayIndex - b.displayIndex);

    thisRow.directChildren = aggregateChildren.map(e => e.id);

    resultRows.push(thisRow);

    for (const child of aggregateChildren) {
      resultRows.push(... child.buildReportRows(depth + 1, specifiedPeriod, fullProject));
    }
    return resultRows;
  }

  toJS() {
    return {
      id: this.id,
      name: this.name,
      plannedStartDate: this.plannedStartDate,
      plannedEndDate: this.plannedEndDate,
      tasks: this.tasks.map((entry) => entry.toJS()),
      taskPackages: this.taskPackages.map((entry) => entry.toJS()),
      periodEntries: this.periodEntries.map((entry) => entry.toJS()),
      analysis: this.analysis.map((entry) => entry.toJS()),
      displayIndex: this.displayIndex,
      code: this.code,
    };
  }

  static fromJS(store, object) {
    const taskPackage = new TaskPackageAnalysis();
    if (object != null) {
      taskPackage.name = object.name;
      taskPackage.code = object.code;
      taskPackage.id = object.id;
      taskPackage.project = store;
      taskPackage.plannedStartDate = object.plannedStartDate;
      taskPackage.plannedEndDate = object.plannedEndDate;
      taskPackage.tasks = object.tasks.map((savedTask) => {
        return TaskAnalysis.fromJS(store, savedTask);
      });
      taskPackage.taskPackages = object.taskPackages.map((savedTaskpackage) => {
        return TaskPackageAnalysis.fromJS(store, savedTaskpackage);
      });
      taskPackage.analysis = object.analysis.map((entry) => {
        return Analysis.fromJS(entry);
      });
      taskPackage.displayIndex = object.displayIndex;
    }
    return taskPackage;
  }

  /* static fromJS_To2DArray(store, taskPackage, evaData, specifiedPeriod) {
    let tasksAndPackages = {};
    let facSum = 0;
    const zeroPercent = percentFormat(0);
    const zeroDollars = currencyFormat(0);
    if (taskPackage != null) {
      taskPackage.tasks.map((savedTask) => {
        let exist = false;
        // Checking the existence of an object.name   // ASK MATT: If the check can be done in a better way
        for (let i = 0; i < evaData.length; i++) {
          if (evaData[i].displayName === " " + object.code + " " + object.name + " ") {
            exist = true;
            break;
          }
        }
        if (!exist) {
          evaData.push({ displayName: " " + object.code + " " + object.name + " " });
        }
        const result = TaskAnalysis.fromJS_To2DArray(
          store,
          savedTask,
          evaData,
          specifiedPeriod
        );

        if (result.fac != null) {
          facSum += result.fac;
        }
        return result;
      });
      taskPackage.taskPackages.map((savedTaskpackage) => {
        let exist = false;
        // Checking the existence of an object.name   // ASK MATT: If the check can be done in a better way
        for (let i = 0; i < evaData.length; i++) {
          if (evaData[i].displayName === " " + taskPackage.code + " " + taskPackage.name + " ") {
            exist = true;
            break;
          }
        }
        if (!exist) {
          evaData.push({ displayName: " " + taskPackage.code + " " + taskPackage.name + " " });
        }
        const result = TaskPackageAnalysis.fromJS_To2DArray(
          store,
          savedTaskpackage,
          evaData,
          specifiedPeriod
        );

        if (result.fac != null) {
          facSum += result.fac;
        }
        return result.evaData;
      });
      taskPackage.analysis = object.analysis.map((entry) => {
        return Analysis.fromJS(entry);
      });

      // This sections of code will only be executed for taskPackages, avoiding all task
      tasksAndPackages.displayName = " " + taskPackage.code + " " + taskPackage.name + " ";
      tasksAndPackages.progress = zeroPercent;
      tasksAndPackages.budgetSpent = zeroPercent;
      tasksAndPackages.plannedProgress = zeroPercent;
      tasksAndPackages.bac = zeroDollars;
      tasksAndPackages.actualCost = zeroDollars;
      tasksAndPackages.actualCostPeriod = zeroDollars;
      tasksAndPackages.cpiCumulativeIcon = "";
      tasksAndPackages.cpiCumulative = 0.0;
      tasksAndPackages.spiCumulativeIcon = "";
      tasksAndPackages.spiCumulative = 0.0;
      // The "facColumn" needs to be checked, not sure about it
      tasksAndPackages.fac = zeroDollars;
      tasksAndPackages.vac = zeroDollars;
      tasksAndPackages.costStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
      tasksAndPackages.schedStatusIndicator = "\u2588\u2588\u2588\u2588\u2588\u2588";
      tasksAndPackages.comment = "";
      tasksAndPackages.sortIndex = object.displayIndex;

      for (const analysis of taskPackage.analysis) {
        if (analysis.period === parseInt(specifiedPeriod, 10)) {
          let result =
            analysis.actualProgress *
            analysis.budgetSpent *
            analysis.plannedProgress;
          let plannedProgressCol = analysis.plannedProgress; // "IFERROR({0}/{1},0)", worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.bacColumn].Address);
          let bacCol = analysis.budgetAtCompletion;
          let progressCol = analysis.actualProgress;
          // The "earnedValueCol" needs to be checked, not sure about it
          let earnedValueCol = analysis.earnedValue; // "{0}+{1}-{2}", worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.bacColumn].Address, worksheet.Cells[row, this.facColumn].Address);
          let actualCostCol = analysis.actualCost;
          let budgetSpentCol = analysis.budgetSpent; // analysis.budgetSpent = ("IFERROR({0}/{1},0)", worksheet.Cells[row, this.].Address, worksheet.Cells[row, this.bacColumn].Address)
          let actualCostPeriodCol = analysis.actualCostPeriod;
          let earnedValuePeriodCol = analysis.earnedValuePeriod;

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let costVarianceCol = analysis.costVariance; // analysis.costVariance = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)
          //let earnedScheduleCol = analysis.earnedSchedule;
          //let scheduleVarianceCol = analysis.scheduleVarianceCost; // analysis.scheduleVarianceCost = ("{0}-{1}", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address)

          // The "cpiCumuCol" needs to be checked, not sure about it as it's being calculated from the eranedValue
          let cpiCumuCol = analysis.costPerformanceIndex; // (analysis.costPerformanceIndex).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),0)", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address)

          let cpiCumuColNeg = "";
          let spiCostCumuColNeg = "";

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let spiTimeCumuColNeg = "";
          if (result !== 0 && !isNaN(result)) {
            // "IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address
            let calcCpi = 0;
            if (actualCostCol - actualCostPeriodCol > 0) {
              calcCpi =
                cpiCumuCol -
                (earnedValueCol - earnedValuePeriodCol) /
                (actualCostCol - actualCostPeriodCol);
            }

            let calcSpiCost =
              analysis.schedulePerformanceIndexCostPeriod -
              analysis.schedulePerformanceIndexCost; // "ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

            // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
            // however as it is not being used in our current version, I have be commented it out
            //let calcSpiTime = analysis.schedulePerformanceIndexTimePeriod - analysis.schedulePerformanceIndexTime; // "(ROUND({0},2)", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex

            if (calcCpi > 0) {
              cpiCumuColNeg = "\u25B2";
            } // "IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>0,\"\u25B2\",IF(IFERROR(ROUND({0}-(({1}-{2})/({3}-{4})),2),0)>=-0.0001,\"\u25AC\",\"\u25BC\"))", worksheet.Cells[row, this.cpiCumuColumn].Address, worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.earnedValuePeriodColumn].Address, worksheet.Cells[row, this.actualCostColumn].Address, worksheet.Cells[row, this.actualCostPeriodColumn].Address)
            else if (calcCpi >= -0.0001) {
              cpiCumuColNeg = "\u25AC";
            } else {
              cpiCumuColNeg = "\u25BC";
            }

            if (calcSpiCost > 0) {
              spiCostCumuColNeg = "\u25B2";
            } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
            else if (calcSpiCost >= -0.0001) {
              spiCostCumuColNeg = "\u25AC";
            } else {
              spiCostCumuColNeg = "\u25BC";
            }

            // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
            // however as it is not being used in our current version, I have be commented it out
            if (calcSpiTime > 0) {
              spiTimeCumuColNeg = "\u25B2";
            } // "IF(ROUND({0},2)>0,\"\u25B2\",IF(ROUND({0},2)>=-0.0001,\"\u25AC\",\"\u25BC\"))", periodWeight.SchedulePerformanceIndexPeriod - periodWeight.SchedulePerformanceIndex
            else if (calcSpiTime >= -0.0001) {
              spiTimeCumuColNeg = "\u25AC";
            } else {
              spiTimeCumuColNeg = "\u25BC";
            }
          }

          // The "spiCostCol" and "spiTimeCol" needs to be checked, not sure about them
          let spiCostCol = analysis.schedulePerformanceIndexCost; // (analysis.schedulePerformanceIndexCost).toFixed(2) = ("IFERROR(ROUND({0}/{1},2),ROUND(1+{2},2))", worksheet.Cells[row, this.earnedValueColumn].Address, worksheet.Cells[row, this.plannedValueColumn].Address, worksheet.Cells[row, this.progressColumn].Address)

          // This valriable is found and has an application in the EVA desktop app, that's why I kept it,
          // however as it is not being used in our current version, I have be commented it out
          //let spiTimeCol = analysis.schedulePerformanceIndexTime;
          let vacCol = analysis.varianceAtCompletionMean;
          let costStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
          let schedStatusCol = "\u2588\u2588\u2588\u2588\u2588\u2588";
          let commentCol = analysis.note;

          tasksAndPackages.progress = percentFormat(progressCol);
          tasksAndPackages.budgetSpent = percentFormat(budgetSpentCol);
          tasksAndPackages.plannedProgress = percentFormat(plannedProgressCol);
          tasksAndPackages.bac = currencyFormat(bacCol);
          tasksAndPackages.actualCost = currencyFormat(actualCostCol);
          tasksAndPackages.actualCostPeriod = currencyFormat(actualCostPeriodCol);
          tasksAndPackages.cpiCumulativeIcon = cpiCumuColNeg;
          tasksAndPackages.cpiCumulative =
            cpiCumuCol != null ? cpiCumuCol.toFixed(2) : cpiCumuCol;
          tasksAndPackages.spiCumulativeIcon = spiCostCumuColNeg;
          tasksAndPackages.spiCumulative =
            spiCostCol != null ? spiCostCol.toFixed(2) : spiCostCol;
          // The "facColumn" needs to be checked, not sure about it
          tasksAndPackages.fac = currencyFormat(facSum);
          tasksAndPackages.vac = currencyFormat(vacCol);
          tasksAndPackages.costStatusIndicator = costStatusCol;
          tasksAndPackages.schedStatusIndicator = schedStatusCol;
          tasksAndPackages.comment = commentCol;
        }
      }

      for (let i = 0; i < evaData.length; i++) {
        if (evaData[i].displayName === " " + object.code + " " + object.name + " ") {
          evaData[i] = tasksAndPackages;
          break;
        }
      }
    }
    return { evaData: evaData, fac: facSum };
  }

  static fromJS_DetermineStyle(store, object, evaStyle, depth) {
    let stylePackages = {};
    const taskPackage = new TaskPackageAnalysis();
    let childrensDepth = depth + 1;
    if (object != null) {
      taskPackage.name = object.name;
      taskPackage.id = object.id;
      taskPackage.project = store;
      taskPackage.tasks = object.tasks.map((savedTask) => {
        let exist = false;
        // Checking the existence of an object.name   // ASK MATT: If the check can be done in a better way
        for (let i = 0; i < evaStyle.length; i++) {
          if (evaStyle[i].displayName === " " + object.code + " " + object.name + " ") {
            exist = true;
            break;
          }
        }
        if (!exist) {
          evaStyle.push({ displayName: " " + object.code + " " + object.name + " " });
        }


        return TaskAnalysis.fromJS_DetermineStyle(
          store,
          savedTask,
          evaStyle,
          childrensDepth
        );
      });
      taskPackage.taskPackages = object.taskPackages.map((savedTaskpackage) => {
        let exist = false;
        // Checking the existence of an object.name   // ASK MATT: If the check can be done in a better way
        for (let i = 0; i < evaStyle.length; i++) {
          if (evaStyle[i].displayName === " " + object.code + " " + object.name + " ") {
            exist = true;
            break;
          }
        }
        if (!exist) {
          evaStyle.push({ displayName: " " + object.code + " " + object.name + " " });
        }

        const result = TaskPackageAnalysis.fromJS_DetermineStyle(
          store,
          savedTaskpackage,
          evaStyle,
          childrensDepth
        );
        return result.evaStyle;
      });

      stylePackages.displayName = " " + object.code + " " + object.name + " ";
      stylePackages.fontSize = Math.max(14 - depth, 11);
      stylePackages.fontStyle = depth < 3 ? "bold" : "plain";
      stylePackages.treeDepth = depth * 1;
      stylePackages.childCount = 0;
      stylePackages.sortIndex = object.displayIndex;

      for (let i = 0; i < evaStyle.length; i++) {
        if (evaStyle[i].displayName === " " + object.code + " " + object.name + " ") {
          evaStyle[i] = stylePackages;
          break;
        }
      }
    }
    return { evaStyle: evaStyle, depth: depth };
  } */
}
